'use strict';

/**
 * AnalyticsUtils: PostHog for the AngularJS app, same behaviour as the Web2 `AnalyticsService`:
 * lazy-loads the SDK, identifies the signed-in user, captures router pageviews, links Sentry
 * events to replays, and mutes everything when the PostHog API is unreachable.
 *
 * `init` is called from `main.js`; the AngularJS run block in `app.js` forwards the Keycloak
 * profile, logout and state changes. Both can happen before the SDK has loaded, so the
 * profile and the first pageview wait for it.
 *
 * Event names follow `<domain>.<entity>.<action>`, without the app prefix: `captureEvent` adds it.
 *
 * @module analytics.analyticsUtils
 */

import * as Sentry from '@sentry/browser';
import { MonitoringUtils } from '../monitoring/monitoringUtils.js';
import { stripAuthParamsFromCapture, stripAuthParamsFromRecordedUrl } from './analyticsSanitize.js';

/**
 * Same PostHog init options as the Web2 apps.
 *
 * Session-recording masking is declared here (not in the PostHog project UI)
 * so sensitive QuartzX surfaces stay masked even if admin-side settings drift.
 */
const POSTHOG_INIT_OPTIONS = {
  person_profiles: 'identified_only',
  capture_pageview: false,
  capture_pageleave: true,
  persistence: 'cookie',
  disable_session_recording: false,
  before_send: stripAuthParamsFromCapture,
  session_recording: {
    // Also receives every page URL the recorder writes into the replay, not only network requests.
    maskCapturedNetworkRequestFn: stripAuthParamsFromRecordedUrl,
    // A custom mask function turns off PostHog's own scrubbing of request and response bodies.
    recordBody: false,
    // Always mask input elements (email, tokens, free-text) in replays.
    maskAllInputs: true,
    // Opt-in mask for sensitive text: add `data-ph-mask` to the element or ancestor.
    maskTextSelector: '[data-ph-mask]',
    // Fully block certain DOM subtrees from being recorded: add `data-ph-ignore`.
    blockSelector: '[data-ph-ignore]',
    recordCrossOriginIframes: false,
  },
};

const state = {
  posthog: null,
  appName: null,
  environment: null,
  isDisabled: false,
  identifiedEmail: null,
  pendingProfile: null,
  hasNavigated: false,
};

function identify(profile) {
  const email = profile && profile.email;

  if (!email || email === state.identifiedEmail) {
    return;
  }

  state.identifiedEmail = email;

  const userName =
    profile.given_name && profile.family_name ? profile.given_name + ' ' + profile.family_name : profile.name;

  state.posthog.identify(email, {
    id: email,
    email: email,
    name: userName || email,
    tenantUid: profile.qxTenantUid || '',
    environment: state.environment,
  });
  state.posthog.register_for_session({ environment: state.environment });
}

/**
 * Kill-switch for environments where the PostHog API is unreachable (corporate proxy,
 * ad-blocker): without it the SDK keeps retrying every captured event and buffering
 * recordings for nothing. Detected via `errorsLoading` on the initial `/flags` call.
 */
function disable(posthog) {
  if (state.isDisabled) {
    return;
  }

  state.isDisabled = true;
  state.posthog = null;

  posthog.stopSessionRecording();
  posthog.opt_out_capturing();

  MonitoringUtils.addBreadcrumb({
    category: 'analytics',
    message: 'posthog disabled: api unreachable (proxy or ad-blocker)',
    level: 'warning',
  });
}

function onLoaded(posthog, sentryOptions) {
  state.posthog = posthog;

  // There is no user-facing PostHog consent flow, so a persisted opt-out can only come from
  // the kill-switch: re-arm on every boot so tracking recovers once the network does.
  if (posthog.has_opted_out_capturing()) {
    posthog.clear_opt_in_out_capturing();
  }

  posthog.register({ app: state.appName });
  posthog.register_for_session({ environment: state.environment });

  posthog.onFeatureFlags(function (_flags, _variants, context) {
    if (context && context.errorsLoading) {
      disable(posthog);
    }
  });

  const posthogIntegration = sentryOptions && sentryOptions.posthogIntegration;

  if (posthogIntegration && posthogIntegration.projectId) {
    Sentry.addIntegration(
      posthog.sentryIntegration({
        organization: posthogIntegration.organization,
        projectId: posthogIntegration.projectId,
        prefix: posthogIntegration.prefix,
        severityAllowList: ['error'],
      })
    );
  }

  if (state.pendingProfile) {
    identify(state.pendingProfile);
    state.pendingProfile = null;
  }

  if (state.hasNavigated) {
    posthog.capture('$pageview');
  }
}

const AnalyticsUtils = {
  /**
   * Load and start PostHog. No-op if the PostHog key or host is empty (local dev, on-premise).
   *
   * @param {{ appName: string, environment: object }} options
   */
  init: function (options) {
    const env = options.environment;
    const apiKey = env.postHog && env.postHog.apiKey;
    const apiHost = env.postHog && env.postHog.apiHost;

    if (!apiKey || !apiHost) {
      return;
    }

    state.appName = options.appName;
    state.environment = env.environment;

    import('posthog-js')
      .then(function (module) {
        const posthog = module.default;

        posthog.init(
          apiKey,
          Object.assign({}, POSTHOG_INIT_OPTIONS, {
            api_host: apiHost,
            loaded: function () {
              onLoaded(posthog, env.sentry);
            },
          })
        );
      })
      .catch(function (err) {
        MonitoringUtils.addBreadcrumb({
          category: 'analytics',
          message: 'posthog-js dynamic import failed',
          level: 'warning',
          data: { error: err instanceof Error ? err.message : String(err) },
        });
      });
  },

  /** @param {object|null} profile Keycloak user info (email, given_name, family_name, name) */
  identify: function (profile) {
    if (state.isDisabled) {
      return;
    }

    if (!state.posthog) {
      state.pendingProfile = profile;
      return;
    }

    identify(profile);
  },

  reset: function () {
    state.pendingProfile = null;

    if (!state.identifiedEmail) {
      return;
    }

    state.identifiedEmail = null;

    if (state.posthog) {
      state.posthog.reset(true);
    }
  },

  capturePageview: function () {
    state.hasNavigated = true;

    if (state.posthog) {
      state.posthog.capture('$pageview');
    }
  },

  captureEvent: function (name, props) {
    if (!state.posthog || !state.appName) {
      return;
    }

    state.posthog.capture(state.appName + '.' + name, props);
  },

  captureException: function (err, props) {
    if (state.posthog) {
      state.posthog.captureException(err, props);
    }
  },
};

export { AnalyticsUtils };
